import { createFileRoute, Link, useSearch, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, useCallback } from "react";
import type { BrowserMultiFormatReader as ZxingReader, IScannerControls } from "@zxing/browser";
// @zxing/browser is loaded lazily (cached) — keeps it out of the route chunk.
let zxingPromise: Promise<typeof import("@zxing/browser")> | null = null;
const loadZxing = () => (zxingPromise ??= import("@zxing/browser"));
import { ArrowLeft, Camera, CheckCircle2, AlertTriangle, Loader2, MapPin, WifiOff, ShieldAlert, CloudUpload, X, Image as ImageIcon, RefreshCw, ZoomIn, ZoomOut, Smartphone, Flashlight, FlashlightOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { ingestScan } from "@/lib/scan-adapters";
import { useOfflineSync } from "@/lib/use-offline-sync";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { fetchActiveRunForCurrentUser, type ActiveRun } from "@/lib/checkpoint-scans";
import { useCameraZoom } from "@/lib/useCameraZoom";
import { useCameraTorch } from "@/lib/useCameraTorch";
import { useIosCameraHint } from "@/lib/useIosCameraHint";
import { ScannerPreferences } from "@/components/ScannerPreferences";

export const Route = createFileRoute("/_authenticated/guard-tours/scan")({
  component: ScanPage,
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});

type Status = "idle" | "requesting" | "scanning" | "denied" | "unsupported" | "error";

const COOLDOWN_MS = 3000;

function ScanPage() {
  const search = useSearch({ from: "/_authenticated/guard-tours/scan" });
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const readerRef = useRef<ZxingReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastScanRef = useRef<{ token: string; at: number } | null>(null);
  const processingRef = useRef(false);

  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastSuccessAt, setLastSuccessAt] = useState<number | null>(null);
  const [silentPanic, setSilentPanic] = useState(false);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const { caps: zoomCaps, zoom, setZoom } = useCameraZoom(activeStream);
  const torch = useCameraTorch(activeStream);
  const [cssZoom, setCssZoom] = useState(1);
  const { show: showIosHint, dismiss: dismissIosHint } = useIosCameraHint();
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [recentScans, setRecentScans] = useState<
    { id: string; name: string; tour: string; at: Date; distance: number | null; queued?: boolean }[]
  >([]);
  const sync = useOfflineSync();

  // Live progress for the active patrol run — checkpoints flip to "scanned" automatically as
  // tags are detected, in any order. Refreshed via Supabase realtime on every new scan row.
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);
  const [progress, setProgress] = useState<{
    checkpoints: { id: string; sequence: number; name: string; scanned_at: string | null }[];
  }>({ checkpoints: [] });

  const refreshActiveProgress = useCallback(async () => {
    const run = await fetchActiveRunForCurrentUser();
    setActiveRun(run);
    if (!run) { setProgress({ checkpoints: [] }); return; }
    const [{ data: cps }, { data: scans }] = await Promise.all([
      supabase.from("tour_checkpoints").select("id,sequence,name").eq("tour_id", run.tour_id).order("sequence"),
      supabase.from("checkpoint_scans").select("checkpoint_id,scanned_at").eq("tour_run_id", run.id),
    ]);
    const last: Record<string, string> = {};
    for (const s of scans ?? []) last[(s as any).checkpoint_id] = (s as any).scanned_at;
    setProgress({
      checkpoints: (cps ?? []).map((c: any) => ({
        id: c.id, sequence: c.sequence, name: c.name, scanned_at: last[c.id] ?? null,
      })),
    });
  }, []);

  useEffect(() => { refreshActiveProgress(); }, [refreshActiveProgress]);

  // Realtime: any new checkpoint_scans row for the active run instantly marks that checkpoint
  // as completed in the list, regardless of sequence.
  useEffect(() => {
    if (!activeRun) return;
    const ch = supabase
      .channel(`scan-progress-${activeRun.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "checkpoint_scans", filter: `tour_run_id=eq.${activeRun.id}` },
        () => refreshActiveProgress(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [activeRun?.id, refreshActiveProgress]);


  const stopScanner = useCallback(() => {
    try { controlsRef.current?.stop(); } catch { /* noop */ }
    controlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setActiveStream(null);
    setCssZoom(1);
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch { /* noop */ }
      videoRef.current.srcObject = null;
    }
  }, []);

  const handleToken = useCallback(
    async (rawPayload: string, method: "qr_camera" | "qr_image" = "qr_camera") => {
      const dedupeKey = rawPayload.trim();
      if (!dedupeKey) return;
      const now = Date.now();
      if (
        lastScanRef.current &&
        lastScanRef.current.token === dedupeKey &&
        now - lastScanRef.current.at < COOLDOWN_MS
      ) {
        return;
      }
      if (processingRef.current) return;
      processingRef.current = true;
      lastScanRef.current = { token: dedupeKey, at: now };

      try {
        const res = await ingestScan({ raw: rawPayload, method, silentPanic });
        if (res.ok && "queued" in res && res.queued) {
          if (!silentPanic) toast.message("Saved offline — will sync when online");
          setRecentScans((prev) =>
            [{ id: res.id, name: "Queued scan", tour: "Pending sync", at: new Date(), distance: null, queued: true }, ...prev].slice(0, 8),
          );
          setLastSuccessAt(Date.now());
        } else if (res.ok && "result" in res && res.result.ok) {
          const r = res.result;
          // Standalone site checkpoints have no tour; show site + occurrence instead.
          const contextLabel = r.tour
            ? r.tour.name
            : [r.site?.name ?? "On-site checkpoint", r.scan_number ? `scan #${r.scan_number} today` : null]
                .filter(Boolean)
                .join(" · ");
          const title = r.standalone
            ? `Checkpoint confirmed: ${r.checkpoint.name}`
            : `Scanned: #${r.checkpoint.sequence} ${r.checkpoint.name}`;
          if (!silentPanic) {
            toast.success(title, {
              description:
                r.distance_m != null ? `${r.distance_m} m from checkpoint` : contextLabel,
            });
          }
          setRecentScans((prev) =>
            [{ id: String(r.scan_id), name: r.standalone ? r.checkpoint.name : `#${r.checkpoint.sequence} ${r.checkpoint.name}`, tour: contextLabel, at: new Date(), distance: r.distance_m }, ...prev].slice(0, 8),
          );

          setLastSuccessAt(Date.now());
          // Optimistic local mark so the UI flips instantly even before realtime fires.
          setProgress((p) => ({
            checkpoints: p.checkpoints.map((c) =>
              c.id === r.checkpoint.id && !c.scanned_at
                ? { ...c, scanned_at: new Date().toISOString() }
                : c,
            ),
          }));
          // Also pull the canonical state (handles auto-created run on first scan).
          refreshActiveProgress();
          if (typeof navigator !== "undefined" && "vibrate" in navigator && !silentPanic) navigator.vibrate?.(80);
        } else if (!res.ok) {
          toast.error(res.error);
        }
      } catch (e: any) {
        toast.error(e?.message ?? "Scan failed");
      } finally {
        setTimeout(() => { processingRef.current = false; }, 800);
      }
    }, [silentPanic, refreshActiveProgress]);

  // CRITICAL: must be called synchronously from a user gesture (button tap) on iOS Safari.
  const startScanner = useCallback(async () => {
    if (typeof window === "undefined") return;

    if (!window.isSecureContext) {
      setStatus("unsupported");
      setErrorMessage("Camera requires a secure (HTTPS) connection.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      setErrorMessage("Camera API is not available in this browser. Try Safari (iOS) or Chrome (Android).");
      return;
    }

    setStatus("requesting");
    setErrorMessage(null);
    setPhotoError(null);

    // Ensure prior session is fully torn down before starting a new one.
    stopScanner();

    const video = videoRef.current;
    if (!video) {
      setStatus("error");
      setErrorMessage("Video element not ready.");
      return;
    }
    // iOS Safari quirks
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("muted", "true");
    video.muted = true;
    video.autoplay = true;

    try {
      // Open camera ourselves with explicit rear-facing constraint so we don't get the front
      // camera (zxing's auto-pick is unreliable on iOS).
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      setActiveStream(stream);
      video.srcObject = stream;
      // play() must be awaited and is allowed because we are still in the gesture-initiated promise chain.
      try { await video.play(); } catch { /* autoplay race — zxing will retry */ }

      const { BrowserMultiFormatReader } = await loadZxing();
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      const controls = await reader.decodeFromVideoElement(video, (result) => {
        if (result) handleToken(result.getText(), "qr_camera");
      });
      controlsRef.current = controls;
      setStatus("scanning");
    } catch (err: any) {
      const name = err?.name || "";
      const msg = err?.message || String(err);
      const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
      stopScanner();
      if (name === "NotAllowedError" || /permission|denied/i.test(msg)) {
        setStatus("denied");
        setErrorMessage(
          isIOSDevice
            ? "Camera access was blocked. Open Settings → Safari → Camera, allow access for this site, then tap Retake."
            : "Camera permission denied. Enable camera access for this site in your browser settings, then tap Retake.",
        );
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setStatus("error");
        setErrorMessage("No usable camera found on this device.");
      } else if (name === "NotReadableError") {
        setStatus("error");
        setErrorMessage("Camera is in use by another app. Close other camera apps and try again.");
      } else {
        setStatus("error");
        setErrorMessage(msg);
      }
    }
  }, [handleToken, stopScanner]);

  // Photo-upload fallback (works even when live camera is blocked)
  const decodeImageFile = useCallback(async (file: File) => {
    setPhotoError(null);
    try {
      const url = URL.createObjectURL(file);
      const { BrowserMultiFormatReader } = await loadZxing();
      const reader = new BrowserMultiFormatReader();
      const result = await reader.decodeFromImageUrl(url);
      URL.revokeObjectURL(url);
      if (result) {
        setPhotoError(null);
        handleToken(result.getText(), "qr_image");
      } else {
        setPhotoError("No QR code found in that photo. Make sure the code is in focus and well-lit, then try again.");
      }
    } catch (e: any) {
      setPhotoError(e?.message ?? "Could not decode image. Try taking a clearer photo.");
    }
  }, [handleToken]);

  // Cleanup on unmount only — do NOT auto-start (iOS requires gesture).
  useEffect(() => {
    return () => stopScanner();
  }, [stopScanner]);

  // Token from URL (deep-link scan): record without needing camera.
  useEffect(() => {
    if (search.token) handleToken(search.token, "qr_camera");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.token]);

  const isLive = status === "scanning";

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-8">
      <Link to="/guard-tours" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to tours
      </Link>
      <h1 className="mb-1 text-2xl font-semibold tracking-tight md:text-3xl">QR scanner</h1>
      <p className="mb-2 text-sm text-muted-foreground">
        Tap <strong>Start scanner</strong> to open your camera. Scans are logged automatically with timestamp and GPS.
      </p>
      <div className="mb-4">
        <ScannerPreferences />
      </div>
      {showIosHint && (
        <div className="relative mb-4 flex items-start gap-2 rounded-lg bg-amber-500/15 border border-amber-500/30 px-3 py-2 text-xs text-amber-200">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <span className="flex-1">iPhone users: tap <strong>Start scanner</strong> below to open the camera. Safari requires a tap to begin scanning.</span>
          <button
            type="button"
            aria-label="Dismiss hint"
            onClick={dismissIosHint}
            className="shrink-0 text-amber-400 hover:text-amber-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="relative aspect-square w-full bg-black sm:aspect-video">
          {/* Always mounted so the ref is available the moment the user taps Start */}
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-cover transition-transform"
            style={!zoomCaps.supported && cssZoom !== 1 ? { transform: `scale(${cssZoom})`, transformOrigin: "center" } : undefined}
            playsInline
            muted
            autoPlay
          />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-2/3 w-2/3 max-w-xs rounded-2xl border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>

          <button
            type="button"
            aria-label="Close scanner"
            onClick={() => { stopScanner(); navigate({ to: "/guard-tours" }); }}
            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur transition hover:bg-black/80"
          >
            <X className="h-5 w-5" />
          </button>

          {isLive && torch.supported && (
            <button
              type="button"
              aria-label={torch.on ? "Turn flashlight off" : "Turn flashlight on"}
              onClick={torch.toggle}
              className={`absolute right-16 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full shadow-lg backdrop-blur transition ${torch.on ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-black/60 text-white hover:bg-black/80"}`}
            >
              {torch.on ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
            </button>
          )}

          {status === "idle" && (
            <Overlay
              icon={<Camera className="h-10 w-10" />}
              text="Camera is off. Tap Start scanner to begin."
              action={
                <Button size="lg" onClick={startScanner} className="gap-2">
                  <Camera className="h-4 w-4" /> Start scanner
                </Button>
              }
            />
          )}
          {status === "requesting" && <Overlay icon={<Loader2 className="h-8 w-8 animate-spin" />} text="Requesting camera…" />}
          {status === "denied" && (
            <Overlay
              role="alert"
              icon={<AlertTriangle className="h-8 w-8" />}
              text={errorMessage ?? "Camera permission denied"}
              action={<Button size="sm" variant="secondary" onClick={startScanner}><RefreshCw className="h-4 w-4" aria-hidden="true" /> Try again</Button>}
            />
          )}
          {status === "unsupported" && (
            <Overlay role="alert" icon={<AlertTriangle className="h-8 w-8" />} text={errorMessage ?? "Camera not supported"} />
          )}
          {status === "error" && (
            <Overlay
              role="alert"
              icon={<AlertTriangle className="h-8 w-8" />}
              text={errorMessage ?? "Camera error"}
              action={<Button size="sm" variant="secondary" onClick={startScanner}><RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry</Button>}
            />
          )}

          {!silentPanic && lastSuccessAt && Date.now() - lastSuccessAt < 1500 && (
            <div role="status" className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 animate-in fade-in zoom-in">
              <div className="flex items-center gap-2 rounded-full bg-emerald-500/95 px-4 py-2 text-sm font-medium text-white shadow-lg">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Scan successful
              </div>
            </div>
          )}
          {isLive && (() => {
            const min = zoomCaps.supported ? zoomCaps.min : 1;
            const max = zoomCaps.supported ? zoomCaps.max : 3;
            const step = zoomCaps.supported ? zoomCaps.step : 0.1;
            const value = zoomCaps.supported ? zoom : cssZoom;
            const apply = (v: number) => zoomCaps.supported ? setZoom(v) : setCssZoom(v);
            return (
              <div className="absolute inset-x-3 bottom-3 z-10 flex items-center gap-2 rounded-full bg-black/65 px-3 py-2 backdrop-blur">
                <button type="button" aria-label="Zoom out" onClick={() => apply(Math.max(min, +(value - step).toFixed(2)))} className="text-white/90 hover:text-white">
                  <ZoomOut className="h-4 w-4" />
                </button>
                <Slider min={min} max={max} step={step} value={[value]} onValueChange={(v) => apply(v[0] ?? min)} className="flex-1" aria-label="Camera zoom" />
                <button type="button" aria-label="Zoom in" onClick={() => apply(Math.min(max, +(value + step).toFixed(2)))} className="text-white/90 hover:text-white">
                  <ZoomIn className="h-4 w-4" />
                </button>
                <span className="w-10 text-right text-[10px] font-mono text-white/80">{value.toFixed(1)}x</span>
              </div>
            );
          })()}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t p-3 text-xs text-muted-foreground">
          <span role="status" className="flex items-center gap-1.5">
            <Camera className="h-3.5 w-3.5" aria-hidden="true" />
            {isLive ? "Live — looking for QR codes" : status}
          </span>
          <div className="flex items-center gap-2">
            {!sync.online && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-500">
                <WifiOff className="mr-1 h-3 w-3" /> Offline
              </Badge>
            )}
            {sync.pending > 0 && (
              <Badge variant="outline" className="border-primary/40 text-primary">
                <CloudUpload className="mr-1 h-3 w-3" /> {sync.pending} queued
              </Badge>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) decodeImageFile(f);
                e.target.value = "";
              }}
            />
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} title="Decode a photo of the QR code">
              <ImageIcon className="h-3.5 w-3.5" /> Photo
            </Button>
            <Button
              size="sm"
              variant={silentPanic ? "destructive" : "outline"}
              onClick={() => setSilentPanic((v) => !v)}
              title="Next scan triggers a silent duress alert"
            >
              <ShieldAlert className="h-3.5 w-3.5" /> Panic {silentPanic ? "armed" : "off"}
            </Button>
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> GPS when available
            </span>
          </div>
        </div>
      </Card>

      {photoError && (
        <div role="alert" className="mt-4 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Scan failed</p>
            <p className="text-sm text-destructive/80">{photoError}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="shrink-0 gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Retake
          </Button>
        </div>
      )}

      {activeRun && progress.checkpoints.length > 0 && (() => {
        const done = progress.checkpoints.filter((c) => c.scanned_at).length;
        const total = progress.checkpoints.length;
        const pct = Math.round((done / total) * 100);
        return (
          <Card className="mt-6 p-4">
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Active patrol</div>
                <div className="font-medium">{activeRun.tour_name}</div>
              </div>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-500">
                {done} / {total} scanned
              </Badge>
            </div>
            <Progress
              value={pct}
              className="mt-3 h-2"
              aria-label="Checkpoint progress"
              aria-valuetext={`${done} of ${total} checkpoints scanned, ${pct} percent`}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Scan tags in any order — each one is matched and marked complete automatically.
            </p>
            <div className="mt-3 grid max-h-56 gap-1 overflow-y-auto pr-1" role="list" aria-label="Checkpoints on this patrol">
              {progress.checkpoints.map((c) => (
                <div
                  key={c.id}
                  role="listitem"
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition ${
                    c.scanned_at ? "bg-emerald-500/10 text-foreground" : "text-muted-foreground"
                  }`}
                >
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${
                    c.scanned_at ? "bg-emerald-500/20 text-emerald-500" : "bg-muted"
                  }`}>
                    {c.scanned_at ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> : c.sequence}
                    <span className="sr-only">
                      {c.scanned_at ? "Scanned." : `Checkpoint ${c.sequence}, not yet scanned.`}
                    </span>
                  </div>
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.scanned_at && (
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(c.scanned_at).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      <h2 className="mt-8 mb-3 text-sm font-medium text-muted-foreground">Recent scans (this session)</h2>
      {recentScans.length === 0 ? (
        <p className="text-sm text-muted-foreground">No scans yet.</p>
      ) : (
        <Card className="divide-y" role="list" aria-label="Recent scans this session">
          {recentScans.map((s) => (
            <div key={s.id} role="listitem" className="flex items-center justify-between gap-3 p-3 text-sm">
              <div>
                <div className="font-medium">{s.name}</div>
                <div className="text-xs text-muted-foreground">
                  {s.tour} · {s.at.toLocaleTimeString()}
                  {s.distance != null && ` · ${s.distance} m`}
                </div>
              </div>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden="true" />
              <span className="sr-only">Recorded</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function Overlay({
  icon, text, action, role = "status",
}: { icon: React.ReactNode; text: string; action?: React.ReactNode; role?: "status" | "alert" }) {
  return (
    <div className="absolute inset-0 z-[5] flex flex-col items-center justify-center gap-3 bg-black/70 p-6 text-center text-white">
      <span aria-hidden="true">{icon}</span>
      <p role={role} className="max-w-sm text-sm">{text}</p>
      {action}
    </div>
  );
}
